import React from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import BrandLogo from "../branding/BrandLogo";
import BrandThemeProvider from "../branding/BrandThemeProvider";
import { APP_TOKENS } from "./publicFormTokens";

const DEFAULT_NEXT_STEPS = [
  "Our team reviews your submission and the documents you uploaded.",
  "We'll reach out at the email address you provided if we need anything else.",
  "You'll hear from us with next steps on your financing request.",
];

/**
 * Terminal state after a successful submit. Sets expectations about what happens
 * next rather than just confirming receipt — this is the applicant's last
 * screen, and there is no portal login waiting for them on the other side.
 */
const PublicFormSuccess = ({
  hideLogo,
  customLogoName,
  title = "Submission received",
  message = "Thank you — your submission has been sent to our team.",
  nextSteps = DEFAULT_NEXT_STEPS,
}) => (
  // LENDMARQ DIVERGENCE: the success screen renders OUTSIDE PublicFormShell, so
  // it needs the brand-blue palette in its own right — see BrandThemeProvider.
  <BrandThemeProvider>
  <Flx
    centerX
    centerY
    sx={{ minHeight: "100vh", background: APP_TOKENS.canvas, px: 2, py: 6 }}
  >
    <Flx column centerY gap={4} sx={{ maxWidth: 540, width: "100%" }}>
      {hideLogo ? null : <BrandLogo height={34} maxWidth={200} />}
      {customLogoName && (
        <Txt component="div" sx={{ fontWeight: 700, color: APP_TOKENS.ink }}>
          {customLogoName}
        </Txt>
      )}

      <Box
        sx={{
          background: APP_TOKENS.surface,
          border: `1px solid ${APP_TOKENS.border}`,
          borderRadius: APP_TOKENS.radius,
          p: 4,
          width: "100%",
        }}
      >
        <Flx column centerY gap={2}>
          <CheckCircleRounded sx={{ fontSize: 44, color: "success.main" }} />
          <Txt
            component="h1"
            sx={{ m: 0, fontSize: 22, fontWeight: 700, color: APP_TOKENS.ink }}
          >
            {title}
          </Txt>
          <Txt
            component="div"
            sx={{ color: APP_TOKENS.inkMuted, textAlign: "center" }}
          >
            {message}
          </Txt>
        </Flx>

        <Flx
          column
          gap={1.5}
          sx={{ mt: 4, pt: 3, borderTop: `1px solid ${APP_TOKENS.border}` }}
        >
          <Txt
            component="div"
            sx={{ fontWeight: 600, fontSize: 14, color: APP_TOKENS.ink }}
          >
            What happens next
          </Txt>
          {nextSteps.map((text, i) => (
            <Flx key={text} centerY gap={1.5}>
              <Flx
                centerX
                centerY
                sx={{
                  width: 20,
                  height: 20,
                  flexShrink: 0,
                  borderRadius: "50%",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "primary.main",
                  background: (theme) =>
                    alpha(theme.palette.primary.main, 0.1),
                }}
              >
                {i + 1}
              </Flx>
              <Txt
                component="div"
                sx={{ fontSize: 13.5, color: APP_TOKENS.inkMuted }}
              >
                {text}
              </Txt>
            </Flx>
          ))}
        </Flx>
      </Box>
    </Flx>
  </Flx>
  </BrandThemeProvider>
);

export default PublicFormSuccess;
